// ── Serviço de consumo da API do Hub (api-bluehub) ────────────────
// O que este serviço acrescenta ao gateway são as TRAVAS que o contrato da
// API exige e que nenhuma tela deveria ter de lembrar:
//
//   · provisionar-vidas SOMA vagas, e vaga não se remove por esta API;
//   · a venda só fecha com `encerrar_prospeccao: true`, que exige ao menos
//     uma vaga na MESMA chamada;
//   · 200 na cobrança não quer dizer que cobrou;
//   · a Idempotency-Key é por operação, nunca por lead.
//
// Regra de camada: aqui não há fetch, Supabase nem React — só a porta.

import type {
  IBluehubGateway, BluehubIdempotencia, BluehubFalha,
} from '../ports/IBluehubGateway';
import type {
  BluehubNovaEmpresa, BluehubCorrecaoEmpresa, BluehubItemVidas,
  BluehubPessoaConvidada, BluehubMarca, BluehubPlano,
} from '../ports/types';
import { lerDesfechoCobranca, atingiuOPiso, PISO_VIDAS_FATURAVEIS } from './cobranca';

interface Alvo {
  empresaId: string;
  empresaPublicId: string;
}

export class BluehubService {
  constructor(private readonly gateway: IBluehubGateway) {}

  // ── Identidade da empresa ────────────────────────────────────────

  /** Cadastra a empresa como LEAD. Dispare CEDO (na entrada da Formalização):
   *  enquanto a conta é lead, o alcance da chave fica garantido para sempre e
   *  provisionar não cobra ninguém. Esperar a venda é apostar que ninguém
   *  criou a empresa no Hub por outro caminho nesse meio-tempo. */
  criarEmpresa = (input: { empresaId: string; empresa: BluehubNovaEmpresa; idempotencia?: BluehubIdempotencia }) =>
    this.gateway.criarEmpresa({
      empresaId: input.empresaId,
      empresa: input.empresa,
      idempotencia: input.idempotencia ?? { entidadeId: input.empresaId },
    });

  /** Corrige o cadastro. A chave é por CORREÇÃO, não por empresa: reusá-la faz
   *  a segunda correção não ser aplicada, com 200 e `ja_existia: true`.
   *  ⚠️ `endereco` e `representante_legal` substituem o bloco por inteiro —
   *  monte-os completos, do banco, mesmo para trocar um campo só. */
  atualizarEmpresa = (input: Alvo & { campos: BluehubCorrecaoEmpresa; idempotencia: BluehubIdempotencia }) =>
    this.gateway.atualizarEmpresa(input);

  // ── Catálogo ────────────────────────────────────────────────────

  /** Os slugs que ESTA empresa aceita. Nunca mantenha tabela de slug à mão.
   *  ⚠️ Consome o teto de 1.000/h (600/min) da chave de escrita — cacheie pela duração da
   *  negociação em vez de reler a cada tentativa. */
  listarPlanos = (alvo: Alvo) => this.gateway.listarPlanos(alvo);

  // ── Vidas ───────────────────────────────────────────────────────

  /**
   * Provisiona vagas. 🔴 SOMA: repetir com chave NOVA soma de novo, e não há
   * endpoint de remoção. Passe `totalVagasConhecido` (de `cs.hub_contas` ou da
   * leitura de reconciliação) e o serviço recusa o lote que ultrapassaria o
   * que se pretende ter — a trava que protege a mensalidade do cliente.
   */
  provisionarVidas = (input: Alvo & {
    vidas: BluehubItemVidas[];
    encerrarProspeccao?: boolean;
    idempotencia: BluehubIdempotencia;
    totalVagasConhecido?: number;
    totalVagasPretendido?: number;
  }) => {
    const doLote = input.vidas.reduce((soma, item) => soma + Number(item.quantidade || 0), 0);

    if (input.totalVagasPretendido != null && input.totalVagasConhecido != null) {
      const depois = input.totalVagasConhecido + doLote;
      if (depois > input.totalVagasPretendido) {
        throw new Error(
          `Este lote levaria a conta a ${depois} vagas, e o pretendido é ${input.totalVagasPretendido}. ` +
          'Vaga provisionada não se remove por esta API — confira o total antes de somar.',
        );
      }
    }

    // A venda fecha com `encerrar_prospeccao: true`, e o Hub exige ao menos
    // uma vaga na mesma chamada: guarde a última vaga real do mix para ela.
    if (input.encerrarProspeccao && doLote < 1) {
      throw new Error('Fechar a venda exige ao menos uma vaga nesta mesma chamada.');
    }

    return this.gateway.provisionarVidas(input);
  };

  /** Dá nome e e-mail às vagas. Não cria vaga: sem vaga livre do plano é 409.
   *  Valide os slugs contra `listarPlanos` ANTES de montar o lote — a ordem
   *  das recusas num lote misto não serve como diagnóstico. */
  convidarVidas = (input: Alvo & { vidas: BluehubPessoaConvidada[]; idempotencia: BluehubIdempotencia }) =>
    this.gateway.convidarVidas(input);

  // ── Contrato e cobrança ─────────────────────────────────────────

  /** Cunha o envelope e dispara o e-mail de assinatura. É irreversível e vai
   *  para uma pessoa real. Exige representante — que só existe depois de
   *  alguém RESGATAR o `convite_url`; preencher o signatário não dispensa. */
  enviarContrato = (input: Alvo & { idempotencia: BluehubIdempotencia }) =>
    this.gateway.enviarContrato(input);

  /** Antecipa a cobrança consolidada. Devolve o desfecho JÁ LIDO: `cobrou`,
   *  a ação a tomar e se o e-mail do boleto saiu do lado do Hub. */
  emitirCobranca = async (input: Alvo & { idempotencia: BluehubIdempotencia }) => {
    const cobranca = await this.gateway.emitirCobranca(input);
    return { cobranca, desfecho: lerDesfechoCobranca(cobranca) };
  };

  // ── Acesso ──────────────────────────────────────────────────────

  /** Repõe o acesso de quem JÁ resgatou o convite (link vencido, senha
   *  esquecida, troca de aparelho). O link NÃO volta na resposta (`acesso_url` é
   *  sempre null): chega só por e-mail. Confira `notificacao_email` — se for
   *  `falhou`, o e-mail não saiu. O replay devolve o desfecho com `ja_existia`. */
  solicitarAcesso = (input: Alvo & { email: string; idempotencia: BluehubIdempotencia }) =>
    this.gateway.solicitarAcesso(input);

  // ── Reconciliação ───────────────────────────────────────────────

  /** As vagas do lado do Hub, por status. Não há webhook de entrada: é assim
   *  que o resgate do convite e a vaga que virou `assigned` chegam até nós. */
  lerVidasDaEmpresa = (input: { empresaId: string }) => this.gateway.lerVidasDaEmpresa(input);

  /** As cobranças do lado do Hub — leitura, nunca grava em `cs.competencias`. */
  lerCobrancasDaEmpresa = (input: { empresaId: string }) => this.gateway.lerCobrancasDaEmpresa(input);

  // ── Sincronização do espelho (/clientes, aba Hub) ────────────────

  /** Relê todas as empresas do Hub. Não é por empresa — é o botão
   *  "Atualizar agora" da tela de Clientes. */
  sincronizarEmpresas = () => this.gateway.sincronizarEmpresas();
}

// ── Regras puras, usáveis sem o gateway ────────────────────────────

/** Quantas vagas faltam para chegar ao pretendido. Nunca negativo: a diferença
 *  para menos não se resolve por esta API, é pedido à ED. */
export const vagasAProvisionar = (pretendido: number, totalAtual: number): number =>
  Math.max(0, Math.trunc(pretendido) - Math.trunc(totalAtual));

/** A venda pode fechar nesta marca com este número de vidas faturáveis?
 *  No BlueHub, abaixo de 3 a cobrança responde 200 e nunca cobra. */
export function podeFecharVenda(marca: BluehubMarca, vidasFaturaveis: number): {
  pode: boolean; piso: number; faltam: number; motivo: string | null;
} {
  const piso = PISO_VIDAS_FATURAVEIS[marca] ?? 0;
  const pode = atingiuOPiso(marca, vidasFaturaveis);
  return {
    pode,
    piso,
    faltam: Math.max(0, piso - vidasFaturaveis),
    motivo: pode ? null
      : `A marca ${marca} exige ${piso} vidas faturáveis; esta venda tem ${vidasFaturaveis}. ` +
        'Abaixo do piso a cobrança responde sucesso e não cobra.',
  };
}

/** Os slugs do lote existem no catálogo desta empresa? Conferir aqui evita o
 *  422 cujo lugar na fila não é previsível num lote misto. */
export const slugsForaDoCatalogo = (slugs: string[], catalogo: BluehubPlano[]): string[] => {
  const validos = new Set(catalogo.map(p => p.slug));
  return [...new Set(slugs)].filter(slug => !validos.has(slug));
};

/** Texto para a tela, a partir da falha. O que decide o fluxo é `codigo` e a
 *  régua de retry; isto é só o que a pessoa lê. */
export function explicarFalha(falha: BluehubFalha): string {
  switch (falha.codigo) {
    case 'escopo':
      return 'A chave do CRM não alcança esta operação. É configuração nossa — repetir não resolve.';
    case 'credencial':
      return 'A chave do Hub não autentica. Confira se é a chave do ambiente certo.';
    case 'nao_encontrado':
      return 'O Hub não encontrou a empresa — ela pode não existir, estar fora do alcance desta chave, ou a integração estar desligada.';
    case 'teto':
      return falha.retryAfterSegundos
        ? `Teto de chamadas do Hub atingido. Tente de novo em ${falha.retryAfterSegundos}s.`
        : 'Teto de chamadas do Hub atingido.';
    case 'provedor':
      // A orientação vem das flags da régua de retry, não do código: o mesmo
      // `provedor` chega como 502 da borda (repetir com a MESMA chave) ou do
      // provedor (chave anterior ficou `failed`: chave NOVA).
      if (falha.repetirComChaveNova) {
        return `${falha.mensagem} Refaça a operação — o sistema vai usar uma chave nova.`;
      }
      if (falha.repetirComMesmaChave) {
        return `${falha.mensagem} Pode repetir a operação: a mesma chave devolve o desfecho da primeira tentativa, sem duplicar.`;
      }
      return falha.mensagem;
    case 'validacao':
      return falha.detalhes?.length
        ? falha.detalhes.map(d => (d.campo ? `${d.campo}: ${d.mensagem}` : d.mensagem)).join(' · ')
        : falha.mensagem;
    case 'rede':
      return `${falha.mensagem} O efeito é incerto: confira antes de repetir.`;
    default:
      return falha.mensagem;
  }
}
